import Phaser from 'phaser';

/**
 * Hand-drawn vector glyphs for chrome iconography — gear, trophy, speaker, note,
 * shake, phone, medal. Each draws *centered at (cx, cy)* into a supplied
 * Graphics so the same icon reads identically in a HUD button, a Settings row,
 * or a Leaderboard badge. Sized in a ~24px box at scale 1; pass `s` to rescale.
 *
 * Kept as plain draw functions (not GameObjects) so callers own depth, rotation,
 * and lifecycle. Colors come from the active palette at the call site.
 */
type G = Phaser.GameObjects.Graphics;

/** Settings cog — the canonical Shmerge gear, shared by HUD + menu. */
export function drawGear(g: G, cx: number, cy: number, color: number, s = 1, holeColor?: number): void {
  const cogR = 8.5 * s;
  const toothR = 12.5 * s;
  const toothHalfA = 0.22;
  const teeth = 8;
  const v: Array<[number, number]> = [];
  for (let i = 0; i < teeth; i++) {
    const aMid = (i / teeth) * Math.PI * 2;
    const aNext = ((i + 1) / teeth) * Math.PI * 2;
    v.push([Math.cos(aMid - toothHalfA) * cogR, Math.sin(aMid - toothHalfA) * cogR]);
    v.push([Math.cos(aMid - toothHalfA) * toothR, Math.sin(aMid - toothHalfA) * toothR]);
    v.push([Math.cos(aMid + toothHalfA) * toothR, Math.sin(aMid + toothHalfA) * toothR]);
    v.push([Math.cos(aMid + toothHalfA) * cogR, Math.sin(aMid + toothHalfA) * cogR]);
    const aGap = (aMid + toothHalfA + aNext - toothHalfA) / 2;
    v.push([Math.cos(aGap) * cogR, Math.sin(aGap) * cogR]);
  }
  g.fillStyle(color, 1);
  g.beginPath();
  g.moveTo(cx + v[0][0], cy + v[0][1]);
  for (let i = 1; i < v.length; i++) g.lineTo(cx + v[i][0], cy + v[i][1]);
  g.closePath();
  g.fillPath();
  if (holeColor != null) {
    g.fillStyle(holeColor, 1);
    g.fillCircle(cx, cy, 4 * s);
  }
}

/** Trophy cup — leaderboard. */
export function drawTrophy(g: G, cx: number, cy: number, color: number, s = 1): void {
  const u = (n: number) => n * s;
  g.fillStyle(color, 1);
  // Bowl: flat top tapering to a rounded base.
  g.beginPath();
  g.moveTo(cx - u(9), cy - u(11));
  g.lineTo(cx + u(9), cy - u(11));
  g.lineTo(cx + u(7), cy - u(4));
  g.arc(cx, cy - u(4), u(7), 0, Math.PI, false);
  g.lineTo(cx - u(9), cy - u(11));
  g.closePath();
  g.fillPath();
  // Side handles.
  g.lineStyle(u(2), color, 1);
  g.beginPath();
  g.arc(cx - u(9), cy - u(8), u(4.5), Math.PI * 1.55, Math.PI * 0.45, false);
  g.strokePath();
  g.beginPath();
  g.arc(cx + u(9), cy - u(8), u(4.5), Math.PI * 0.55, Math.PI * 1.45, true);
  g.strokePath();
  // Stem + tiered base.
  g.fillStyle(color, 1);
  g.fillRect(cx - u(1.6), cy + u(2), u(3.2), u(5));
  g.fillRoundedRect(cx - u(5), cy + u(6), u(10), u(2.4), u(1));
  g.fillRoundedRect(cx - u(7.5), cy + u(8), u(15), u(3.4), u(1.6));
}

/** Speaker with sound waves — SFX row. */
export function drawSpeaker(g: G, cx: number, cy: number, color: number, s = 1): void {
  const u = (n: number) => n * s;
  g.fillStyle(color, 1);
  g.fillRect(cx - u(10), cy - u(4), u(5), u(8));
  g.beginPath();
  g.moveTo(cx - u(5), cy - u(4));
  g.lineTo(cx + u(1), cy - u(9));
  g.lineTo(cx + u(1), cy + u(9));
  g.lineTo(cx - u(5), cy + u(4));
  g.closePath();
  g.fillPath();
  g.lineStyle(u(2), color, 1);
  g.beginPath();
  g.arc(cx + u(2), cy, u(5), -Math.PI / 3, Math.PI / 3, false);
  g.strokePath();
  g.beginPath();
  g.arc(cx + u(2), cy, u(9), -Math.PI / 3.2, Math.PI / 3.2, false);
  g.strokePath();
}

/** Beamed eighth-notes — Music row. */
export function drawNote(g: G, cx: number, cy: number, color: number, s = 1): void {
  const u = (n: number) => n * s;
  g.fillStyle(color, 1);
  g.fillEllipse(cx - u(5), cy + u(6), u(8), u(6));
  g.fillEllipse(cx + u(7), cy + u(3), u(8), u(6));
  g.fillRect(cx - u(2), cy - u(9), u(2.4), u(15));
  g.fillRect(cx + u(9.6), cy - u(12), u(2.4), u(15));
  // Beam joining the two stems.
  g.beginPath();
  g.moveTo(cx - u(2), cy - u(9));
  g.lineTo(cx + u(12), cy - u(12));
  g.lineTo(cx + u(12), cy - u(8));
  g.lineTo(cx - u(2), cy - u(5));
  g.closePath();
  g.fillPath();
}

/** A square jostled by motion lines — Screen Shake row. */
export function drawShake(g: G, cx: number, cy: number, color: number, s = 1): void {
  const u = (n: number) => n * s;
  g.lineStyle(u(2.2), color, 1);
  g.strokeRoundedRect(cx - u(5), cy - u(5), u(10), u(10), u(2));
  g.lineStyle(u(2), color, 0.85);
  g.lineBetween(cx - u(11), cy - u(3.5), cx - u(8), cy - u(3.5));
  g.lineBetween(cx - u(11), cy + u(3.5), cx - u(8), cy + u(3.5));
  g.lineBetween(cx + u(8), cy - u(3.5), cx + u(11), cy - u(3.5));
  g.lineBetween(cx + u(8), cy + u(3.5), cx + u(11), cy + u(3.5));
}

/** Phone outline — Device Tilt row + HUD tilt button. Caller may rotate. */
export function drawPhone(g: G, cx: number, cy: number, color: number, s = 1): void {
  const u = (n: number) => n * s;
  g.lineStyle(u(2), color, 0.95);
  g.strokeRoundedRect(cx - u(6), cy - u(11), u(12), u(22), u(2.5));
  g.fillStyle(color, 0.18);
  g.fillRoundedRect(cx - u(6), cy - u(11), u(12), u(22), u(2.5));
  g.fillStyle(color, 0.9);
  g.fillCircle(cx, cy - u(8), u(1.4));
}

/** A ribboned medallion disc — leaderboard rank badges. */
export function drawMedal(g: G, cx: number, cy: number, r: number, disc: number, ring: number): void {
  // Ribbon tails behind the disc.
  g.fillStyle(ring, 0.55);
  g.fillTriangle(cx - r * 0.7, cy - r * 0.2, cx - r * 0.1, cy + r * 1.4, cx + r * 0.2, cy + r * 0.4);
  g.fillTriangle(cx + r * 0.7, cy - r * 0.2, cx + r * 0.1, cy + r * 1.4, cx - r * 0.2, cy + r * 0.4);
  // Disc + rim.
  g.fillStyle(disc, 1);
  g.fillCircle(cx, cy, r);
  g.lineStyle(Math.max(1.5, r * 0.18), ring, 1);
  g.strokeCircle(cx, cy, r);
  // Inner gleam.
  g.fillStyle(0xffffff, 0.22);
  g.fillCircle(cx - r * 0.28, cy - r * 0.3, r * 0.3);
}
